/**
 * Authentication Service — LogTo OIDC
 *
 * Wraps the Electron IPC bridge for LogTo PKCE authentication.
 * All token management happens in the main process; the renderer
 * only sees user info and auth state events.
 */

import type { AuthUser } from '../../../types/electron';

export type { AuthUser };

export type OAuthProvider = 'google' | 'github';

export type AuthEvent = 'SIGNED_IN' | 'SIGNED_OUT' | 'TOKEN_REFRESHED' | 'AUTH_STALE';

export interface AuthResult {
  success: boolean;
  user?: AuthUser;
  error?: string;
}

// LogTo configuration (renderer-side env vars)
const LOGTO_ENDPOINT = import.meta.env.VITE_LOGTO_ENDPOINT || '';
const LOGTO_APP_ID = import.meta.env.VITE_LOGTO_APP_ID || '';

/**
 * Check if LogTo auth is configured.
 */
export const isAuthConfigured = (): boolean => Boolean(LOGTO_ENDPOINT && LOGTO_APP_ID);

const LOGTO_RESOURCE = import.meta.env.VITE_LOGTO_RESOURCE || '';
if (isAuthConfigured() && !LOGTO_RESOURCE) {
  console.warn('[AuthService] VITE_LOGTO_RESOURCE is not configured — server-side token validation may fail.');
}

// ─── Auth State Event Emitter ────────────────────────────────────────

type AuthEventCallback = (event: AuthEvent) => void;
const listeners = new Set<AuthEventCallback>();

function emitAuthEvent(event: AuthEvent): void {
  for (const cb of listeners) {
    try {
      cb(event);
    } catch (err) {
      console.error('[AuthService] Listener error:', err);
    }
  }
}

/**
 * Subscribe to auth state changes.
 * Returns an unsubscribe function.
 */
export const onAuthStateChange = (
  callback: AuthEventCallback
): (() => void) => {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
};

// Wire up IPC listener → local event emitter (when in Electron)
let ipcListenerSetup = false;

function ensureIpcListener(): void {
  if (ipcListenerSetup) return;
  ipcListenerSetup = true;

  if (!window.electronAPI?.auth?.onAuthStateChanged) return;

  window.electronAPI.auth.onAuthStateChanged((data) => {
    emitAuthEvent(data.event);
  });
}

// ─── Auth Operations ─────────────────────────────────────────────────

/**
 * Initialize auth state (called on app start).
 * Returns the user if a valid session exists.
 */
export const initialize = async (): Promise<AuthUser | null> => {
  ensureIpcListener();

  if (!window.electronAPI?.auth?.initialize) return null;

  try {
    const result = await window.electronAPI.auth.initialize();
    return result.user ?? null;
  } catch {
    return null;
  }
};

/**
 * Start sign-in flow. Opens LogTo auth page in system browser.
 * With LogTo, all auth methods (email/password, Google, GitHub) are handled
 * on LogTo's hosted page. The optional `directSignIn` param can skip to
 * a specific social provider (e.g., 'social:google').
 */
export const signIn = async (options?: { directSignIn?: string }): Promise<AuthResult> => {
  if (!window.electronAPI?.auth?.signIn) {
    return { success: false, error: 'Auth not available' };
  }

  try {
    const result = await window.electronAPI.auth.signIn(options);
    if (result.success) {
      return { success: true };
    }
    return { success: false, error: result.error };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Sign in failed',
    };
  }
};

/**
 * Sign in with OAuth provider (convenience wrapper).
 * Maps provider to LogTo's direct_sign_in parameter.
 */
export const signInWithOAuth = async (
  provider: OAuthProvider
): Promise<AuthResult> => {
  return signIn({ directSignIn: `social:${provider}` });
};

/**
 * Sign up — with LogTo, signup is handled on LogTo's hosted page.
 * This just opens the sign-in page (LogTo shows register option).
 */
export const signUp = async (
  _email: string,
  _password: string
): Promise<AuthResult> => {
  // LogTo handles registration on its hosted page
  return signIn();
};

/**
 * Sign out the current user.
 */
export const signOut = async (): Promise<AuthResult> => {
  if (!window.electronAPI?.auth?.signOut) {
    return { success: false, error: 'Auth not available' };
  }

  try {
    const result = await window.electronAPI.auth.signOut();
    if (result.success) {
      emitAuthEvent('SIGNED_OUT');
      return { success: true };
    }
    return { success: false, error: result.error };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Sign out failed',
    };
  }
};

/**
 * Get the current access token for API calls.
 * Main process handles refresh automatically.
 */
export const getAccessToken = async (): Promise<string | null> => {
  if (!window.electronAPI?.auth?.getAccessToken) return null;

  try {
    const result = await window.electronAPI.auth.getAccessToken();
    return result.token;
  } catch {
    return null;
  }
};

/**
 * Get the current user. For LogTo, returns from the last known state.
 * @deprecated Use authStore.user instead
 */
export const getUser = async (): Promise<AuthUser | null> => {
  return initialize();
};

/**
 * Get the current session.
 * @deprecated With LogTo, session management is in the main process.
 */
export const getSession = async (): Promise<{ user: AuthUser } | null> => {
  const user = await initialize();
  return user ? { user } : null;
};
